import React, { Component, Fragment } from 'react'
import { connect } from 'react-redux'

import { gtag_report_conversion } from '@utilities/analytics'
import { addSchema, getValidator, formatErrors } from '@utilities/validation'
import { IInquiry } from '@features/inquiries'
import { formatCurrency } from '@utilities/numbers'
// @ts-ignore
import scrollToEl from 'scroll-to-element'

import MortgageInquiryForm from './MortgageInquiryForm'
import Answer from './Answer'
import { IFormValues } from './types'

interface IProps {
  dispatch: Function
  inquiry: IInquiry
  requestInquiry: (calculatorId: string, inquiry: IInquiry) => any
}

interface IState {
  isCalculating: boolean
  apiErrors: null | { [key: string]: string }
}

function mapStateToProps(state: any) {
  return {
    inquiry: state.inquiries.byId.MORTGAGE_INQUIRY
  }
}

function serialize(values: IFormValues, inquiry: IInquiry) {
  const newInquiry = {
    ...inquiry,
    property_purchase_price: values.property_purchase_price,
    deposit: values.deposit,
    num_dependants: values.num_dependants,
    monthly_child_care_costs: values.monthly_child_care_costs,
    primary_applicant: {
      annual_gross_income: values.applicant_1_annual_gross_income,
      monthly_loan_payments: values.monthly_loan_payments,
      monthly_other_outgoings: values.monthly_other_outgoings
    }
  }

  // If num_applicants is '1' then ensure the 2nd applicant is undefined before posting to API
  if (values.num_applicants === '1') {
    newInquiry.secondary_applicant = undefined
  } else {
    newInquiry.secondary_applicant = {
      annual_gross_income: values.applicant_2_annual_gross_income
    }
  }

  return newInquiry
}

const initialFormState: any = {
  property_purchase_price: {
    name: 'property_purchase_price',
    focussed: false,
    touched: false,
    value: null,
    error: null
  },
  deposit: {
    name: 'deposit',
    focussed: false,
    touched: false,
    value: null,
    error: null
  },
  num_applicants: {
    name: 'num_applicants',
    focussed: false,
    touched: false,
    value: '1',
    error: null
  },
  num_dependants: {
    name: 'num_dependants',
    focussed: false,
    touched: false,
    value: null,
    error: null
  },
  applicant_1_annual_gross_income: {
    name: 'applicant_1_annual_gross_income',
    focussed: false,
    touched: false,
    value: null,
    error: null
  },
  applicant_2_annual_gross_income: {
    name: 'applicant_2_annual_gross_income',
    focussed: false,
    touched: false,
    value: null,
    error: null
  },
  monthly_loan_payments: {
    name: 'monthly_loan_payments',
    focussed: false,
    touched: false,
    value: null,
    error: null
  },
  monthly_child_care_costs: {
    name: 'monthly_child_care_costs',
    focussed: false,
    touched: false,
    value: null,
    error: null
  },
  monthly_other_outgoings: {
    name: 'monthly_other_outgoings',
    focussed: false,
    touched: false,
    value: 0,
    error: null
  }
}

class App extends Component<IProps, IState> {
  schemaId: string = '/static/schema/calculators/MortgageInquiryForm.schema.json'
  oaf_extended_constraints: any = {}
  formContainerEl: any = null

  constructor(props: any) {
    super(props)
    addSchema(this.schemaId)
    const mortgageSchemaId = '/static/schema/MortgageInquiry.schema.json'
    addSchema(mortgageSchemaId).then((mortgageSchema) => {
      const oafExtendedConstraints = mortgageSchema.oaf_extended_constraints.split(' ')
      const regex = /(.*)\((.*)\)/
      const re = new RegExp(regex)
      for (const constraint of oafExtendedConstraints) {
        const matches = constraint.match(re)
        const fnName = matches[1]
        this.oaf_extended_constraints[fnName] = {
          fnName,
          args: matches[2].split(',').map((arg: string) => arg.trim())
        }
      }
    })

    this.state = {
      isCalculating: false,
      apiErrors: null
    }
  }

  extendedConstraintsValidation = (values: any) => {
    const errors: any = {}

    /* Minimum Joint Income validation */
    if (this.oaf_extended_constraints.minimum_joint_income) {
      const combinedIncome =
        values.applicant_1_annual_gross_income + values.applicant_2_annual_gross_income
      const minCombinedIncome = this.oaf_extended_constraints.minimum_joint_income.args[0]
      const combinedIncomeMsg = `Combined income must exceed the minimum of ${formatCurrency(
        parseFloat(minCombinedIncome),
        0
      )}.`
      if (
        values.num_applicants === '1' &&
        values.applicant_1_annual_gross_income < minCombinedIncome
      ) {
        errors.applicant_1_annual_gross_income = combinedIncomeMsg
      } else if (values.num_applicants === '2' && combinedIncome < minCombinedIncome) {
        errors.applicant_1_annual_gross_income = combinedIncomeMsg
        errors.applicant_2_annual_gross_income = combinedIncomeMsg
      }
    }

    /* Maximum Deposit validation */
    if (values.property_purchase_price && this.oaf_extended_constraints.maximum_deposit) {
      if (values.deposit > values.property_purchase_price) {
        errors.deposit = 'Deposit must not be greater than the house price.'
      }
    }

    return errors
  }

  validate = (values: any) => {
    let errors: any = {}

    // Required validation
    const requiredFields: Array<string> = [
      'deposit',
      'num_dependants',
      'monthly_loan_payments',
      'monthly_other_outgoings',
      'applicant_1_annual_gross_income'
    ]
    if (values.num_applicants === '2') {
      requiredFields.push('applicant_2_annual_gross_income')
    }
    if (values.num_dependants > 0) {
      requiredFields.push('monthly_child_care_costs')
    }
    for (const fieldName of requiredFields) {
      if (values[fieldName] === null || values[fieldName] === undefined) {
        if (!errors) {
          errors = {}
        }
        errors[fieldName] = 'This field is required.'
      }
    }

    // JSON schema validation
    const validator = getValidator(this.schemaId)
    if (validator) {
      validator(values)
      errors = {
        ...(errors || {}),
        ...formatErrors(validator.errors),
        ...this.extendedConstraintsValidation(values)
      }
    }

    if (Object.keys(errors).length < 1) {
      // explicitly return null because that's what ajv returns for no errors
      errors = null
    }
    return errors
  }

  handleApiErrors = (errors: any) => {
    if (errors) {
      this.setState({
        apiErrors: errors
      })
      scrollToEl(this.formContainerEl, {
        offset: -150,
        ease: 'out-quart',
        duration: 800
      })
    }
  }

  handleSubmit = (values: IFormValues) => {
    const { inquiry, requestInquiry } = this.props

    // Google Analytics and Adwords
    gtag_report_conversion('use_calculator', 'MrXgCODvkZsBEI3lzOMC')

    // Don't submit form again once it's already calculating
    // NOTE there is now a duplication here with inquiry.status
    if (this.state.isCalculating) {
      return null
    }

    this.setState({ isCalculating: true })
    requestInquiry(inquiry.calculatorId, serialize(values, inquiry))
      .then(() => {
        this.setState({ isCalculating: false })
      })
      .catch(this.handleApiErrors)
  }

  render() {
    let { inquiry } = this.props
    let { apiErrors } = this.state
    let { status, answer } = inquiry

    return (
      <Fragment>
        <section className="Section -mod-spacing">
          <div className="Wrapper -wide">
            <h1 className="Heading -one -mod-margin-bottom">What can you afford?</h1>

            <div className="Text -large -mod-wrap -mod-margin-bottom-3">
              <p>
                Get a rough idea of the maximum value of an OnStep home you can afford, based on
                your financial situation and personal circumstances.
              </p>
            </div>

            {status === 'FAILED' && (
              <div className="Wrapper -narrow -mod-margin-bottom-3">
                <div className="Notice -has-icon">
                  <i className="fas fa-exclamation-triangle" />
                  <h2 className="Heading -four">Error</h2>
                  <p>
                    Sorry, there was a problem with your inquiry. Please check the errors below.
                  </p>
                </div>
              </div>
            )}

            <div ref={(el) => (this.formContainerEl = el)} className="Wrapper -narrow">
              {apiErrors ? (
                <div className="Row -mod-margin-bottom-3">
                  <div className="Notice -has-icon">
                    <i className="fas fa-exclamation-triangle" />
                    <p>
                      Sorry, there was a problem with your inquiry.
                      <br />
                      Please contact customer.care@onstep.co to resolve the issue.
                    </p>
                  </div>
                </div>
              ) : null}

              <MortgageInquiryForm
                initialState={initialFormState}
                status={status}
                validate={this.validate}
                onSubmit={this.handleSubmit}
              />
            </div>
          </div>
        </section>

        {answer && status === 'COMPLETE' && <Answer inquiry={inquiry} />}
      </Fragment>
    )
  }
}

export default connect(mapStateToProps)(App)
